// src/pages/ArtistBooking.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import PermissionGuard from "../components/PermissionGuard";
import {
  FiEye,
  FiTrash2,
  FiUser,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiClock,
  FiCreditCard,
  FiTag,
  FiCheck,
  FiSearch,
  FiFilter,
} from "react-icons/fi";

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  ok: "#16a34a",
  warn: "#b91c1c",
  page: "#f5f6f8",
};

const STATUS_RANK = { pending: 0, accepted: 1, completed: 2 };

function statusChipStyle(status) {
  const base = {
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    display: "inline-block",
    border: "1px solid transparent",
  };
  if (status === "pending")
    return { ...base, color: "#92400e", background: "#fef3c7", borderColor: "#fcd34d" };
  if (status === "accepted")
    return { ...base, color: "#1e3a8a", background: "#dbeafe", borderColor: "#93c5fd" };
  if (status === "completed")
    return { ...base, color: "#065f46", background: "#d1fae5", borderColor: "#86efac" };
  return { ...base, color: COLORS.text, background: "#f1f5f9", borderColor: "#e2e8f0" };
}

export default function ArtistBooking() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  // permissions
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // inline change spinner
  const [changing, setChanging] = useState({}); // { [id]: boolean }

  // viewer modal
  const [viewer, setViewer] = useState({ open: false, data: null });

  // UI controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | pending | accepted | completed

  useEffect(() => {
    const emp = JSON.parse(localStorage.getItem("employee"));
    const role = emp?.role;
    const perms = emp?.permissions || [];
    setCanEdit(role === "super_admin" || perms.includes("manage_events") || perms.includes("edit_event"));
    setCanDelete(role === "super_admin" || perms.includes("delete_event"));
  }, []);

  useEffect(() => {
    const qy = query(collection(db, "artist_new_bookings"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map((d) => {
          const x = d.data() || {};
          return {
            id: d.id,
            bookedToStageName: x.bookedToStageName || "—",
            bookedToArtistUid: x.bookedToArtistUid || "—",
            bookedToPhone: x.bookedToPhone || "—",
            bookedByName: x.bookedByName || "—",
            bookedByUsername: x.bookedByUsername || "—",
            bookedByPhone: x.bookedByPhone || "—",
            bookedByUid: x.bookedByUid || "—",
            contactName: x.contactName || "—",
            contactPhone: x.contactPhone || "—",
            eventType: x.eventType || "—",
            eventAddress: x.eventAddress || "—",
            dateFrom: x.dateFrom || "—",
            dateTo: x.dateTo || "—",
            timeFrom: x.timeFrom || "—",
            timeTo: x.timeTo || "—",
            subtotal: x.subtotal ?? null,
            gst: x.gst ?? null,
            total: x.total ?? null,
            paymentId: x.paymentId || "",
            paymentStatus: x.paymentStatus || "",
            paymentSuccess: !!x.paymentSuccess,
            partialPaymentStatus: x.partialPaymentStatus || "",
            paymentMadeAt: x.paymentMadeAt || null,
            createdAt: x.createdAt || null,
            status: String(x.status || "pending").toLowerCase(),
            specialNote: x.specialNote || "",
            eventStarted: !!x.eventStarted,
            eventStartedAt: x.eventStartedAt || null,
            eventCompleted: !!x.eventCompleted,
            eventCompletedAt: x.eventCompletedAt || null,
            startPin: x.startPin || "",
            endPin: x.endPin || "",
            totalHours: x.totalHours ?? null,
          };
        });
        setRows(list);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setErr("Failed to load bookings.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filteredSorted = useMemo(() => {
    let list = [...rows];

    // search across artist, booked by, phones, ids, address, payment id
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const hay = [
          r.id,
          r.bookedToStageName,
          r.bookedToArtistUid,
          r.bookedToPhone,
          r.bookedByName,
          r.bookedByUsername,
          r.bookedByPhone,
          r.eventAddress,
          r.paymentId,
          r.eventType,
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    if (statusFilter !== "All") list = list.filter((r) => r.status === statusFilter);

    // pending → accepted → completed, then newest within status
    return list.sort((a, b) => {
      const ar = STATUS_RANK[a.status] ?? 99;
      const br = STATUS_RANK[b.status] ?? 99;
      if (ar !== br) return ar - br;
      return (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0);
    });
  }, [rows, search, statusFilter]);

  const counts = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r) => r.status === "pending").length;
    const accepted = rows.filter((r) => r.status === "accepted").length;
    const completed = rows.filter((r) => r.status === "completed").length;
    return { total, pending, accepted, completed };
  }, [rows]);

  function openViewer(r) {
    setViewer({ open: true, data: r });
  }
  function closeViewer() {
    setViewer({ open: false, data: null });
  }

  async function applyStatus(id, next) {
    try {
      setChanging((m) => ({ ...m, [id]: true }));
      await updateDoc(doc(db, "artist_new_bookings", id), {
        status: String(next || "pending").toLowerCase(),
      });
      showToast("Status updated");
    } catch (e) {
      console.error(e);
      showToast("Update failed");
    } finally {
      setChanging((m) => ({ ...m, [id]: false }));
    }
  }

  async function remove(id) {
    if (!canDelete) return;
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "artist_new_bookings", id));
      showToast("Booking deleted");
    } catch (e) {
      console.error(e);
      showToast("Delete failed");
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 1600);
  }

  return (
    <PermissionGuard requiredPermission="view_artist_bookings">
      <div className="aa-pro-page" style={{ background: COLORS.page, minHeight: "100vh", fontFamily: "Inter, system-ui, sans-serif" }}>
        <div className="aa-pro-hero" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 className="aa-pro-title">Artist Bookings</h2>
            <p className="aa-pro-subtitle" style={{ marginTop: 6 }}>
              Pending first, then accepted and completed. Live updates from Firestore.
            </p>
          </div>
          <div className="aa-pro-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, minWidth: 240 }}>
            <StatCard label="Total" value={counts.total} />
            <StatCard label="Pending" value={counts.pending} tone="pending" />
            <StatCard label="Accepted" value={counts.accepted} tone="accepted" />
            <StatCard label="Completed" value={counts.completed} tone="completed" />
          </div>
        </div>

        <div className="aa-pro-toolbar" style={controlsRow}>
          <div style={searchBox}>
            <FiSearch style={{ opacity: 0.7 }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search artist, user, phone, booking id, address, payment id…"
              style={searchInput}
            />
          </div>
          <div style={controlGroup}>
            <FiFilter />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={select}>
              <option>All</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {loading && <div style={{ color: COLORS.subtext, marginTop: 10 }}>Loading…</div>}
        {err && <div style={{ color: COLORS.warn, marginTop: 10 }}>{err}</div>}

        {!loading && !err && (
          <div className="aa-pro-table-shell" style={{ marginTop: 12, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  <Th sticky>Booking ID</Th>
                  <Th sticky>Artist</Th>
                  <Th sticky>Booked By</Th>
                  <Th sticky>Date</Th>
                  <Th sticky>Time</Th>
                  <Th sticky>Location</Th>
                  <Th sticky>Payment</Th>
                  <Th sticky>Status</Th>
                  <Th sticky>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filteredSorted.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 14, color: COLORS.subtext }}>
                      No bookings found.
                    </td>
                  </tr>
                ) : (
                  filteredSorted.map((r, idx) => {
                    const rowBg = idx % 2 ? "#fafafa" : "#fff";
                    return (
                      <tr
                        key={r.id}
                        style={{ borderTop: `1px solid ${COLORS.border}`, background: rowBg, transition: "background .15s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}
                      >
                        <Td title={r.id} style={{ fontFamily: "monospace" }}>
                          {r.id.slice(0, 6)}…{r.id.slice(-4)}
                        </Td>
                        <Td>
                          <div style={{ display: "grid" }}>
                            <strong>{r.bookedToStageName}</strong>
                            <span style={{ color: COLORS.subtext, fontSize: 12 }}>
                              UID: {shorten(r.bookedToArtistUid)}
                              {r.bookedToPhone ? ` • ${r.bookedToPhone}` : ""}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <div style={{ display: "grid" }}>
                            <span>
                              <FiUser /> {r.bookedByName} ({r.bookedByUsername})
                            </span>
                            <span style={{ color: COLORS.subtext, fontSize: 12 }}>
                              <FiPhone /> {r.bookedByPhone}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <div>
                            <FiCalendar /> {r.dateFrom}
                            {r.dateTo && r.dateTo !== r.dateFrom ? ` → ${r.dateTo}` : ""}
                            <div style={{ color: COLORS.subtext, fontSize: 12 }}>
                              Created {formatDateTime(r.createdAt) || "—"}
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <FiClock /> {r.timeFrom} – {r.timeTo}
                        </Td>
                        <Td>
                          <div>
                            <FiMapPin /> {r.eventAddress}
                            <div style={{ color: COLORS.subtext, fontSize: 12 }}>{r.eventType}</div>
                          </div>
                        </Td>
                        <Td>
                          <div>
                            <FiCreditCard /> {r.paymentStatus || "—"}
                            <div style={{ color: COLORS.subtext, fontSize: 12 }}>
                              <FiTag /> Total: {money(r.total)}
                              {r.paymentId ? ` • ${r.paymentId}` : ""}
                              {r.paymentSuccess ? " • success" : ""}
                              {r.partialPaymentStatus ? ` • ${r.partialPaymentStatus}` : ""}
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={statusChipStyle(r.status)}>
                              {String(r.status || "").charAt(0).toUpperCase() + String(r.status || "").slice(1)}
                            </span>
                            <StatusSelect id={r.id} initial={r.status} disabled={!canEdit} />
                          </div>
                        </Td>
                        <Td>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button style={iconBtn} title="View" onClick={() => openViewer(r)}>
                              <FiEye />
                            </button>
                            <button
                              style={{ ...iconBtn, color: canDelete ? COLORS.warn : COLORS.subtext }}
                              title={canDelete ? "Delete" : "No permission"}
                              onClick={() => canDelete && remove(r.id)}
                              disabled={!canDelete}
                            >
                              <FiTrash2 />
                            </button>
                            <button
                              style={applyBtn}
                              disabled={!canEdit || !!changing[r.id]}
                              title={canEdit ? "Apply status" : "No permission"}
                              onClick={() => {
                                const el = document.getElementById(`status-${r.id}`);
                                const next = el ? el.value : r.status;
                                applyStatus(r.id, next);
                              }}
                            >
                              <FiCheck />
                              {changing[r.id] ? "Updating…" : "Update"}
                            </button>
                          </div>
                        </Td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Viewer Modal */}
        {viewer.open && viewer.data && (
          <div style={modalBackdrop}>
            <div style={modalCard}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, alignItems: "center" }}>
                <div style={{ fontWeight: 900, color: COLORS.text }}>Booking details</div>
                <button onClick={closeViewer} style={iconBtn} aria-label="Close">
                  ✕
                </button>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                <Row label="Booking ID" value={viewer.data.id} mono />
                <Row label="Artist" value={`${viewer.data.bookedToStageName} (${viewer.data.bookedToPhone || "—"})`} />
                <Row label="Artist UID" value={viewer.data.bookedToArtistUid} mono />
                <Row
                  label="Booked By"
                  value={`${viewer.data.bookedByName} (${viewer.data.bookedByUsername}) — ${viewer.data.bookedByPhone}`}
                />
                <Row label="Contact" value={`${viewer.data.contactName} / ${viewer.data.contactPhone}`} />
                <Row label="Event Type" value={viewer.data.eventType} />
                <Row label="Address" value={viewer.data.eventAddress} />
                <Row
                  label="Date"
                  value={
                    viewer.data.dateFrom +
                    (viewer.data.dateTo && viewer.data.dateTo !== viewer.data.dateFrom
                      ? ` → ${viewer.data.dateTo}`
                      : "")
                  }
                />
                <Row label="Time" value={`${viewer.data.timeFrom} – ${viewer.data.timeTo}`} />
                <Row
                  label="Amount"
                  value={`Subtotal: ${money(viewer.data.subtotal)} | GST: ${money(viewer.data.gst)} | Total: ${money(
                    viewer.data.total
                  )}`}
                />
                <Row
                  label="Payment"
                  value={`${viewer.data.paymentStatus || "—"}${
                    viewer.data.paymentSuccess ? " (success)" : ""
                  }${viewer.data.partialPaymentStatus ? `, ${viewer.data.partialPaymentStatus}` : ""}${
                    viewer.data.paymentId ? ` • ${viewer.data.paymentId}` : ""
                  }`}
                />
                <Row label="Payment at" value={formatDateTime(viewer.data.paymentMadeAt) || "—"} />
                <Row
                  label="Event Pins"
                  value={`Start: ${viewer.data.startPin || "—"} | End: ${viewer.data.endPin || "—"}`}
                />
                <Row
                  label="Event Progress"
                  value={`Started: ${viewer.data.eventStarted ? "Yes" : "No"} ${
                    viewer.data.eventStartedAt ? `(${formatDateTime(viewer.data.eventStartedAt)})` : ""
                  } • Completed: ${viewer.data.eventCompleted ? "Yes" : "No"} ${
                    viewer.data.eventCompletedAt ? `(${formatDateTime(viewer.data.eventCompletedAt)})` : ""
                  }`}
                />
                <Row label="Hours" value={viewer.data.totalHours != null ? `${viewer.data.totalHours}h` : "—"} />
                <Row label="Created" value={formatDateTime(viewer.data.createdAt) || "—"} />
                <Row label="Special Note" value={viewer.data.specialNote || "—"} />
              </div>
            </div>
          </div>
        )}

        {toast && <div style={toastBox}>{toast}</div>}
      </div>
    </PermissionGuard>
  );
}

/* Parts */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, style }) {
  return (
    <td style={{ padding: 10, color: COLORS.text, verticalAlign: "top", ...style }}>{children}</td>
  );
}

function StatusSelect({ id, initial, disabled }) {
  const [val, setVal] = useState(String(initial || "pending").toLowerCase());
  return (
    <select
      id={`status-${id}`}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      disabled={disabled}
      style={selectStyle}
    >
      <option value="pending">Pending</option>
      <option value="accepted">Accepted</option>
      <option value="completed">Completed</option>
    </select>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10 }}>
      <div style={{ color: COLORS.subtext, fontWeight: 700 }}>{label}</div>
      <div style={{ color: COLORS.text, fontFamily: mono ? "monospace" : "inherit" }}>{value}</div>
    </div>
  );
}

/* Helpers */
function shorten(s = "", len = 6) {
  if (!s || s.length <= len + 4) return s || "—";
  return `${s.slice(0, len)}…${s.slice(-4)}`;
}

function toMillis(ts) {
  try {
    if (!ts) return 0;
    if (ts?.toDate) return ts.toDate().getTime();
    if (typeof ts === "number") return ts;
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}

function formatDateTime(v) {
  try {
    if (!v) return "";
    const d = typeof v === "string" ? new Date(v) : v.toDate ? v.toDate() : new Date(v);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  } catch {
    return "";
  }
}
function money(v) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
      v
    );
  } catch {
    return `₹${v}`;
  }
}

/* Styles */
const controlsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  marginTop: 12,
  marginBottom: 12,
};
const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 280,
  flex: 1,
};
const searchInput = {
  border: "none",
  outline: "none",
  flex: 1,
  fontSize: 14,
};
const controlGroup = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "8px 10px",
  background: "#fff",
};
const select = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  paddingRight: 4,
  cursor: "pointer",
};

const iconBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  width: 34,
  height: 34,
  borderRadius: 8,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

const applyBtn = {
  height: 34,
  padding: "0 10px",
  borderRadius: 8,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const selectStyle = {
  height: 34,
  padding: "0 10px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
};

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  zIndex: 60,
  padding: 16,
};

const modalCard = {
  width: "100%",
  maxWidth: 760,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
};

const toastBox = {
  position: "fixed",
  bottom: 16,
  right: 16,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  boxShadow: "0 8px 24px rgba(0,0,0,.12)",
  color: COLORS.text,
  fontWeight: 800,
  zIndex: 80,
};

function StatCard({ label, value, tone }) {
  const palette =
    tone === "pending"
      ? { bg: "#fff7ed", border: "#fed7aa", text: "#92400e" }
      : tone === "accepted"
      ? { bg: "#eff6ff", border: "#bfdbfe", text: "#1e3a8a" }
      : tone === "completed"
      ? { bg: "#ecfdf5", border: "#a7f3d0", text: "#065f46" }
      : { bg: "#fff", border: COLORS.border, text: COLORS.text };
  return (
    <div
      className="aa-pro-metric"
      style={{
        background: tone ? palette.bg : "rgba(255,255,255,0.14)",
        border: `1px solid ${tone ? palette.border : "rgba(255,255,255,0.24)"}`,
        borderRadius: 14,
        padding: "10px 12px",
        minWidth: 120,
        display: "grid",
        gap: 2,
      }}
    >
      <div style={{ fontSize: 12, color: tone ? COLORS.subtext : "rgba(255,255,255,0.76)" }}>{label}</div>
      <div style={{ fontWeight: 900, color: tone ? palette.text : "#fff", fontSize: 18 }}>{value}</div>
    </div>
  );
}
